import { Portfolio } from "@/types/Portfolio";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useWatchlistAlertMode } from "@/pages/settings/hooks/useWatchlistAlertMode";
import { useHeadlinesFilterStore } from "./HeadlinesFilterStore";
import { useEffect, useRef } from "react";

type NotificationChannel = "sound" | "popup";

/**
 * Watchlists selected by the current Watchlist Alert Mode (off/highlight/
 * filter/both), before any channel filter. Shared by the sound and pop-up
 * selectors so the alert-mode logic lives in one place.
 */
function useAlertModePortfolios(): Portfolio[] {
  const { portfolios } = usePortfoliosCRUD();
  const { watchlistAlertMode } = useWatchlistAlertMode();

  const portfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.portfoliosHighlight
  );
  const portfoliosFilter = useHeadlinesFilterStore(
    (state) => state.portfoliosFilter
  );

  let portfolioIds: number[] = [];
  if (watchlistAlertMode === "off") {
    portfolioIds = [];
  } else if (watchlistAlertMode === "highlight") {
    portfolioIds = portfoliosHighlight;
  } else if (watchlistAlertMode === "filter") {
    portfolioIds = portfoliosFilter;
  } else if (watchlistAlertMode === "both") {
    // Combine and deduplicate
    portfolioIds = [...new Set([...portfoliosHighlight, ...portfoliosFilter])];
  }

  return portfolioIds
    .map((portfolioId) =>
      portfolios.find((portfolio) => portfolio.id === portfolioId)
    )
    .filter((portfolio): portfolio is Portfolio => portfolio !== undefined);
}

/**
 * Returns a ref of alert-mode watchlists that have the given notification
 * channel enabled. Ref so SSE callbacks read current values without re-renders.
 */
function useChannelEnabledRef(channel: NotificationChannel) {
  const portfolios = useAlertModePortfolios();
  const filtered = portfolios.filter(
    (portfolio) => portfolio.notifications[channel]
  );

  const ref = useRef(filtered);
  useEffect(() => {
    ref.current = filtered;
  }, [filtered]);

  return ref;
}

/**
 * Hook that tracks portfolios with sound alerts enabled for Headlines, gated by
 * the user's Watchlist Alert Mode (off/highlight/filter/both).
 */
export function useHeadlinesPortfoliosWithSoundEnabled() {
  return useChannelEnabledRef("sound");
}

/**
 * Hook that tracks portfolios with pop-up (browser notification) alerts enabled
 * for Headlines, gated by the same Watchlist Alert Mode.
 */
export function useHeadlinesPortfoliosWithPopupEnabled() {
  return useChannelEnabledRef("popup");
}
